import { FC, useCallback, useMemo, useState } from "react";
import { Command } from "@vendor/cmdk/cmdk";
import { pluralizeWithCount } from "@common/helpers/string";
import {
    CheckboxIndicator,
    type CheckboxIndicatorState,
} from "@design-system/Checkbox/CheckboxIndicator";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import {
    buildClassSearchTokens,
    ClassWithChildren,
    deselectSiblingsInSameRoot,
} from "@helpers/class";
import { ClassAvatar } from "@components/avatar/ClassAvatar";
import styles from "./Commands.module.scss";

interface ClassRow {
    classItem: ClassWithChildren;
    classId: string;
    depth: number;
    checkboxState: CheckboxIndicatorState;
}

function buildNestedClassOptions({
    node,
    depth,
    selectedClassIds,
}: {
    node: ClassWithChildren;
    depth: number;
    selectedClassIds: Set<string>;
}): ClassRow[] {
    const children = node.children;
    // Assignment, not a filter: only the exact class is checked — selecting
    // never cascades to parents or children, so ancestors show no partial.
    const option: ClassRow = {
        classItem: node,
        classId: node.id,
        depth,
        checkboxState: selectedClassIds.has(node.id) ? "checked" : "unchecked",
    };

    const childOptions = children.flatMap((child) =>
        buildNestedClassOptions({
            node: child,
            depth: depth + 1,
            selectedClassIds,
        }),
    );

    return [option, ...childOptions];
}

interface Props {
    heading: string;
    classTree: ClassWithChildren[];
    classesMap?: Map<string, ClassWithChildren>;
    onApply: (classIds: string[]) => void;
    onRemoveAll?: () => void;
}

export const BulkClassCommandsList: FC<Props> = ({
    heading,
    classTree,
    classesMap,
    onApply,
    onRemoveAll,
}) => {
    const [selectedClassIds, setSelectedClassIds] = useState<Set<string>>(
        new Set(),
    );

    const toggleClass = useCallback(
        (classId: string) => {
            setSelectedClassIds((prev) => {
                if (prev.has(classId)) {
                    const next = new Set(prev);
                    next.delete(classId);
                    return next;
                }
                const next = new Set([...prev, classId]);
                if (!classesMap) {
                    return next;
                }
                return deselectSiblingsInSameRoot({
                    selectedIds: next,
                    addedClassId: classId,
                    classesMap,
                });
            });
        },
        [classesMap],
    );

    const rows = useMemo(
        () =>
            classTree.flatMap((root) =>
                buildNestedClassOptions({
                    node: root,
                    depth: 0,
                    selectedClassIds,
                }),
            ),
        [classTree, selectedClassIds],
    );

    const searchTokensById = useMemo(
        () => buildClassSearchTokens(classTree),
        [classTree],
    );

    const handleApply = useCallback(
        () => onApply(Array.from(selectedClassIds)),
        [onApply, selectedClassIds],
    );

    return (
        <>
            <Command.Group heading={heading}>
                {rows.map((row) => (
                    <Command.Item
                        key={row.classItem.id}
                        value={`class-${row.classItem.id}`}
                        keywords={
                            searchTokensById.get(row.classItem.id) ?? [
                                row.classItem.label,
                            ]
                        }
                        className={styles.commandItem}
                        onSelect={() => toggleClass(row.classId)}
                        onKeyDown={(e) => {
                            if (e.key === " ") {
                                e.preventDefault();
                                toggleClass(row.classId);
                            }
                        }}
                    >
                        {Array.from({ length: row.depth }, (_, level) => (
                            <div
                                key={level}
                                className={styles.classRailContainer}
                            >
                                <div className={styles.classRailLine} />
                            </div>
                        ))}
                        <CheckboxIndicator state={row.checkboxState} />
                        <ClassAvatar
                            class={row.classItem}
                            size="xs"
                            aria-hidden
                        />
                        <BreakableText
                            text={row.classItem.label}
                            showTooltip
                            className={styles.classLabel}
                        />
                    </Command.Item>
                ))}
            </Command.Group>
            <div className={styles.classApplyFooter}>
                {selectedClassIds.size > 0 && (
                    <Button
                        variant="secondary"
                        size="md"
                        onClick={() => setSelectedClassIds(new Set())}
                    >
                        Clear
                    </Button>
                )}
                {selectedClassIds.size === 0 && onRemoveAll && (
                    <Button variant="secondary" size="md" onClick={onRemoveAll}>
                        Remove classes
                    </Button>
                )}
                <WithHotkey
                    hotKey="mod+enter"
                    hotKeyDisplayPosition="inline"
                    hotKeyOptions={{
                        description: "Apply classes",
                        preventDefault: true,
                        enableOnFormTags: true,
                    }}
                >
                    {(hotkeyHint) => (
                        <Button
                            variant="primary"
                            size="md"
                            disabled={selectedClassIds.size === 0}
                            onClick={handleApply}
                            rightIcon={hotkeyHint}
                        >
                            {selectedClassIds.size > 0
                                ? `Apply ${pluralizeWithCount("class", selectedClassIds.size)}`
                                : "Apply"}
                        </Button>
                    )}
                </WithHotkey>
            </div>
        </>
    );
};
